/** The feature heatmap over the service's own screens (10-21, owner: "기능
 *  히트맵을 해당 페이지 이미지랑 히트맵 위치 합성해서 화면상 어느부분을 주로
 *  클릭 했는지 볼수있어 ?"). Each named control's presses are drawn where the
 *  control sits in the screen map the service publishes
 *  (internal/webusage/screenmap.go). The events carry no click positions, so
 *  a mark is a control, not a point. */

import { useId, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type WebUsageMap, type WebUsageReport } from '@/api/client'
import { qk } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { EmptyState, FilterSelect, Segmented, useQueryParam } from '@/ui'
import { fmtNum } from '../format'
import { heat } from './shared'

type Control = WebUsageReport['controls'][number]
type Shot = WebUsageMap['shots'][number]
type Device = Shot['device']

/** The map changes only when the service photographs its screens again. */
export function useScreenMap(id: string) {
  return useQuery({ queryKey: qk.webUsageMap(id), queryFn: () => api.webUsageMap(id), staleTime: 10 * 60_000, retry: false })
}

/** Presses for one device's pictures. A tablet counts with the desktop: it
 *  mostly gets the desktop layout. */
function pressesOn(c: Control, d: Device): number {
  const by = c.by_device ?? {}
  return d === 'phone' ? (by.phone ?? 0) : Object.entries(by).reduce((n, [k, v]) => (k === 'phone' ? n : n + v), 0)
}

const shotId = (s: Shot) => `${s.screen}|${s.state}`
const pct = (v: number, of: number) => `${(100 * v) / of}%`
const names = (s: Shot) => [...new Set(s.boxes.map((b) => b.c))]
/** Counts printed on the picture; the rest are in the list and on hover. */
const PRINTED = 8

export function ScreenHeat({ map, controls }: { map: WebUsageMap; controls: Control[] }) {
  const [devRaw, setDev] = useQueryParam('dev')
  const [shotRaw, setShot] = useQueryParam('shot')
  const [focus, setFocus] = useState('')
  const device: Device = devRaw === 'phone' ? 'phone' : 'desktop'

  const count = useMemo(() => {
    const m = new Map<string, number>()
    for (const c of controls) m.set(`${c.surface}\0${c.screen}\0${c.control}`, pressesOn(c, device))
    return (s: Shot, name: string) => m.get(`${s.surface}\0${s.screen}\0${name}`) ?? 0
  }, [controls, device])

  const shots = map.shots.filter((s) => s.device === device)
  const totals = shots.map((s) => names(s).reduce((n, c) => n + count(s, c), 0))
  const shot = shots.find((s) => shotId(s) === shotRaw) ?? shots[totals.indexOf(Math.max(...totals))] ?? shots[0]

  // Pressed on this screen but in none of its pictures: inside a menu that was not opened, or gone since.
  const pictured = new Set(shot ? shots.filter((s) => s.surface === shot.surface && s.screen === shot.screen).flatMap(names) : [])
  const elsewhere = shot
    ? controls
        .filter((c) => c.surface === shot.surface && c.screen === shot.screen && !pictured.has(c.control) && pressesOn(c, device) > 0)
        .sort((a, b) => pressesOn(b, device) - pressesOn(a, device))
    : []
  const taken = map.captured ? new Date(map.captured) : null

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          label="기기"
          size="sm"
          options={[
            { value: 'desktop', label: '데스크톱', title: '태블릿 포함' },
            { value: 'phone', label: '폰' },
          ]}
          value={device}
          onChange={(v) => setDev(v === 'phone' ? 'phone' : '')}
        />
        {shot && (
          <FilterSelect
            label="화면"
            value={shotId(shot)}
            onChange={setShot}
            options={shots.map((s, i) => ({ value: shotId(s), label: `${s.label} (${fmtNum(totals[i])})` }))}
            className="max-w-[22rem]"
          />
        )}
        {taken && !Number.isNaN(taken.getTime()) && (
          <span className="text-meta text-muted-foreground">
            {taken.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })}에 찍은 화면
          </span>
        )}
      </div>
      {!shot ? (
        <EmptyState title="이 기기의 화면 사진이 없다" />
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_17rem]">
          <Picture key={shot.image} shot={shot} count={(c) => count(shot, c)} focus={focus} onFocus={setFocus} />
          <div className="min-w-0 space-y-3">
            <ControlList shot={shot} count={(c) => count(shot, c)} focus={focus} onFocus={setFocus} />
            {elsewhere.length > 0 && (
              <div className="space-y-1">
                <p className="text-meta text-muted-foreground" title="메뉴 안에 있거나 지금은 없는 기능">
                  사진에 없는 기능
                </p>
                <ul className="flex flex-wrap gap-1.5">
                  {elsewhere.map((c) => (
                    <li key={c.control} className="rounded bg-muted px-1.5 py-0.5 text-meta">
                      <span className="font-mono">{c.control}</span> <span className="tabular-nums text-muted-foreground">{fmtNum(pressesOn(c, device))}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Picture({ shot, count, focus, onFocus }: { shot: Shot; count: (c: string) => number; focus: string; onFocus: (c: string) => void }) {
  const gid = useId()
  const top = Math.max(0, ...names(shot).map(count))
  const loud = new Set(
    names(shot)
      .filter((c) => count(c) > 0)
      .sort((a, b) => count(b) - count(a))
      .slice(0, PRINTED),
  )
  const seen = new Set<string>()
  const heatColor = 'rgb(var(--chart-1))'
  return (
    <div className={cn('max-h-[75vh] overflow-y-auto rounded-lg border border-border bg-card', shot.device === 'phone' && 'mx-auto w-full max-w-[390px]')}>
      <div className="relative" style={{ aspectRatio: `${shot.w} / ${shot.h}` }}>
        <img src={shot.image} alt={`${shot.label} 화면`} className="absolute inset-0 h-full w-full" />
        <svg
          viewBox={`0 0 ${shot.w} ${shot.h}`}
          preserveAspectRatio="none"
          className="pointer-events-none absolute inset-0 h-full w-full"
          aria-hidden="true"
        >
          <defs>
            <radialGradient id={gid}>
              <stop offset="0" style={{ stopColor: heatColor, stopOpacity: 0.85 }} />
              <stop offset="0.55" style={{ stopColor: heatColor, stopOpacity: 0.4 }} />
              <stop offset="1" style={{ stopColor: heatColor, stopOpacity: 0 }} />
            </radialGradient>
          </defs>
          {shot.boxes.map((b, i) => {
            const n = count(b.c)
            if (!n || !top) return null
            const t = n / top
            const r = Math.min(110, Math.max(22, Math.max(b.w, b.h) * 0.8)) * (0.6 + 0.6 * Math.sqrt(t))
            return <circle key={i} cx={b.x + b.w / 2} cy={b.y + b.h / 2} r={r} fill={`url(#${gid})`} opacity={0.35 + 0.65 * t} />
          })}
          {shot.boxes.map((b, i) => {
            const n = count(b.c)
            return (
              <rect
                key={i}
                x={b.x}
                y={b.y}
                width={b.w}
                height={b.h}
                rx={4}
                fill="none"
                vectorEffect="non-scaling-stroke"
                strokeWidth={b.c === focus ? 3 : n ? 1.5 : 1}
                strokeDasharray={n ? undefined : '3 3'}
                style={{ stroke: n || b.c === focus ? heatColor : 'rgb(var(--muted-foreground) / 0.7)' }}
              />
            )
          })}
        </svg>
        {shot.boxes.map((b, i) => {
          const n = count(b.c)
          const first = !seen.has(b.c)
          seen.add(b.c)
          return (
            <span
              key={i}
              title={`${b.c}\n${n ? `${fmtNum(n)}회` : '누르지 않음'}`}
              onMouseEnter={() => onFocus(b.c)}
              onMouseLeave={() => onFocus('')}
              className="absolute"
              style={{ left: pct(b.x, shot.w), top: pct(b.y, shot.h), width: pct(b.w, shot.w), height: pct(b.h, shot.h) }}
            >
              {first && loud.has(b.c) && (
                <span className="pointer-events-none absolute right-0 top-0 -translate-y-1/2 translate-x-1/3 rounded-full bg-card px-1.5 text-micro font-semibold tabular-nums text-foreground shadow-sm ring-1 ring-chart-1">
                  {fmtNum(n)}
                </span>
              )}
            </span>
          )
        })}
      </div>
    </div>
  )
}

function ControlList({ shot, count, focus, onFocus }: { shot: Shot; count: (c: string) => number; focus: string; onFocus: (c: string) => void }) {
  const list = names(shot).sort((a, b) => count(b) - count(a) || a.localeCompare(b))
  const top = Math.max(0, ...list.map(count))
  return (
    <ul aria-label="이 화면의 기능" className="divide-y divide-border rounded-lg border border-border bg-card lg:max-h-[75vh] lg:overflow-y-auto">
      {list.map((c) => {
        const n = count(c)
        return (
          <li
            key={c}
            onMouseEnter={() => onFocus(c)}
            onMouseLeave={() => onFocus('')}
            className={cn('flex items-center justify-between gap-2 px-3 py-1.5', focus === c && 'bg-muted')}
          >
            <span className="min-w-0 truncate font-mono text-table">{c}</span>
            <span className="shrink-0 rounded px-1.5 py-0.5 text-right text-table tabular-nums" style={heat(n, top)}>
              {n > 0 ? fmtNum(n) : <span className="text-muted-foreground">·</span>}
            </span>
          </li>
        )
      })}
    </ul>
  )
}
